// src/screens/MerchOrderConfirmationScreen.js
// ============================================================================
// MERCH ORDER CONFIRMATION SCREEN
//
// Lands after a completed merch purchase. Two paths converge here:
//
//   1. STRIPE path — URL `/merch-order-confirmation?session_id=cs_...`
//      Stripe Checkout redirects users here after payment. The webhook may
//      still be in flight, so we call getMerchOrderStatus (which looks up
//      the merchOrder by session_id) and poll once on "not found" to ride
//      out the webhook race. No Firestore listener — the function is the
//      authoritative status source for this path.
//
//   2. WALLET path — navigated with route.params = { orderId, paidVia: 'wallet' }
//      createMerchWalletOrder is synchronous, so the order is already
//      in its final state by the time we land here. No polling, no
//      function call — read merchOrders/{orderId} directly via Firestore
//      (user-self read rule added 2026-05-30).
//
// Status interpretation (both paths):
//   - 'paid' or 'submitted_to_printful' → success
//   - 'payment_received_fulfillment_failed' → payment OK, fulfillment hiccup
//   - 'pending' (Stripe only) → webhook hasn't fired yet → retry
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons, FontAwesome5 } from '@expo/vector-icons';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function getSessionIdFromUrl(route) {
  // Try route.params first (React Navigation)
  if (route?.params?.session_id) return route.params.session_id;

  // Fall back to URL parsing on web
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('session_id');
    } catch (_e) {
      return null;
    }
  }
  return null;
}

function getOrderIdFromRoute(route) {
  if (route?.params?.orderId) return route.params.orderId;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('orderId');
    } catch (_e) {
      return null;
    }
  }
  return null;
}

function formatCurrency(cents) {
  if (typeof cents !== 'number') return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Map a raw merchOrders Firestore document to the shape the renderer
 * expects. The Stripe-path cloud function returns {shipping: {city, state,
 * zip}} but the doc stores {shippingAddress: {city, state, postal_code}}
 * — flatten both into one shape so the render code below doesn't fork.
 */
function normalizeOrderDoc(docData) {
  if (!docData) return null;
  const ship = docData.shippingAddress || {};
  return {
    orderId: docData.orderId,
    productName: docData.productName,
    productKey: docData.productKey,
    size: docData.size,
    quantity: docData.quantity,
    // Multi-item: lineItems[] is the new canonical breakdown. Legacy
    // single-item orders won't have it and the renderer falls back to
    // the scalar productName/size/quantity above.
    lineItems: Array.isArray(docData.lineItems) ? docData.lineItems : null,
    itemCount: typeof docData.itemCount === 'number' ? docData.itemCount : (docData.quantity || 1),
    totalCents: docData.totalCents,
    status: docData.status,
    paidVia: docData.paidVia || 'stripe',
    walletFromBalanceCents: docData.walletFromBalanceCents || 0,
    walletFromGiftCents: docData.walletFromGiftCents || 0,
    shipping: {
      city: ship.city || '',
      state: ship.state || '',
      zip: ship.postal_code || ship.zip || '',
    },
  };
}

// ----------------------------------------------------------------------------
// Main screen
// ----------------------------------------------------------------------------

export default function MerchOrderConfirmationScreen({ navigation, route }) {
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [retried, setRetried] = useState(false);
  // Auth-after-redirect tracker — Stripe sends users back via a hard
  // page navigation, and on iOS Safari with ITP the auth state can
  // silently fail to restore. We watch onAuthStateChanged with a short
  // grace window so we can show a "sign back in" banner if auth didn't
  // come back. authState: 'pending' | 'authed' | 'lost'.
  const [authState, setAuthState] = useState('pending');

  // Pull both possible identifiers — the screen routes by whichever it finds.
  const sessionId = getSessionIdFromUrl(route);
  const directOrderId = getOrderIdFromRoute(route);
  // Wallet path is signaled either by an explicit paidVia=wallet param OR
  // by an orderId without a session_id (the only way to arrive direct).
  const isWalletPath = route?.params?.paidVia === 'wallet' || (!!directOrderId && !sessionId);

  // Watch auth: if no user resolves within 2.5s we flag the session as
  // lost (Stripe-redirect ITP edge case) so the renderer can surface a
  // re-sign-in CTA. The order itself is server-side so the customer's
  // purchase is safe — this is purely about restoring the in-app session.
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) setAuthState('authed');
    });
    const timer = setTimeout(() => {
      setAuthState((prev) => (prev === 'pending' ? 'lost' : prev));
    }, 2500);
    return () => { unsub(); clearTimeout(timer); };
  }, []);

  const fetchOrder = useCallback(async () => {
    // Wallet / direct-orderId path — read the doc straight from Firestore.
    if (isWalletPath || directOrderId) {
      if (!directOrderId) {
        setError('Missing order ID. Check the link or open from your order history.');
        setLoading(false);
        return;
      }
      try {
        const db = getFirestore();
        const snap = await getDoc(doc(db, 'merchOrders', directOrderId));
        if (!snap.exists()) {
          throw new Error('Order not found.');
        }
        setOrder(normalizeOrderDoc(snap.data()));
        setLoading(false);
      } catch (err) {
        console.error('[MerchOrderConfirmation] direct fetch error:', err);
        setError(err.message || 'Could not load order.');
        setLoading(false);
      }
      return;
    }

    // Stripe path — go through the cloud function and poll once if the
    // webhook hasn't landed yet.
    if (!sessionId) {
      setError('No order session in URL. Did you arrive here directly?');
      setLoading(false);
      return;
    }

    try {
      const functions = getFunctions();
      const getStatus = httpsCallable(functions, 'getMerchOrderStatus');
      const result = await getStatus({ sessionId });
      setOrder(result.data);
      setLoading(false);
    } catch (err) {
      console.error('Order status fetch error:', err);
      if (!retried && err?.message?.includes('not found')) {
        setRetried(true);
        setTimeout(fetchOrder, 2000);
        return;
      }
      setError(err.message || 'Could not load order status.');
      setLoading(false);
    }
  }, [sessionId, directOrderId, isWalletPath, retried]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // ----------------------------------------------------------------------------
  // Render branches
  // ----------------------------------------------------------------------------

  const renderLoading = () => (
    <View style={styles.center}>
      <ActivityIndicator size="large" color="#FCD000" />
      <Text style={styles.loadingText}>Loading your order…</Text>
    </View>
  );

  const renderError = () => (
    <View style={styles.center}>
      <Ionicons name="warning" size={56} color="#f39c12" />
      <Text style={styles.errorTitle}>Couldn't load your order</Text>
      <Text style={styles.errorBody}>{error}</Text>
      <Text style={styles.errorBody}>
        If you paid and don't see your order, email support@critcash.com with
        your Stripe email. We'll fix it.
      </Text>
      <TouchableOpacity
        style={styles.primaryButton}
        onPress={() => navigation.navigate('Store')}
      >
        <Text style={styles.primaryButtonText}>Back to Store</Text>
      </TouchableOpacity>
    </View>
  );

  const renderFulfillmentFailure = () => (
    <View style={styles.card}>
      <Ionicons name="alert-circle" size={48} color="#f39c12" />
      <Text style={styles.cardTitle}>Payment received — we're on it</Text>
      <Text style={styles.cardBody}>
        Your payment went through successfully, but there was a hiccup submitting
        your order to our fulfillment partner. Don't worry — we've been alerted
        and will get this sorted within 24 hours.
      </Text>
      <Text style={styles.cardBody}>
        You'll receive a confirmation email at the address you used at checkout
        once your order is in production. If you don't hear from us within a day,
        email <Text style={styles.linkText}>support@critcash.com</Text> with your
        order ID.
      </Text>
      {order?.orderId && (
        <Text style={styles.orderIdLine}>Order ID: {order.orderId}</Text>
      )}
      <TouchableOpacity
        style={styles.primaryButton}
        onPress={() => navigation.navigate('Store')}
      >
        <Text style={styles.primaryButtonText}>Back to Store</Text>
      </TouchableOpacity>
    </View>
  );

  const renderSuccess = () => {
    const ship = order?.shipping;
    return (
      <>
        {/* Session-lost-after-Stripe-redirect banner. iOS Safari with ITP
            sometimes drops the auth session when navigating back from a
            cross-domain Stripe Checkout. Order is still safe — it was
            saved server-side before the redirect — but the user appears
            logged out in the header. Surface a clear path to sign back in
            so they don't bounce. */}
        {authState === 'lost' ? (
          <View style={styles.reauthBanner}>
            <Ionicons name="information-circle" size={20} color="#FCD000" style={{ marginRight: 8 }} />
            <View style={{ flex: 1 }}>
              <Text style={styles.reauthBannerText}>
                Your sign-in didn't carry over from checkout.
                Your order is saved — sign back in to see it in Your Orders.
              </Text>
              <TouchableOpacity
                style={styles.reauthBannerBtn}
                onPress={() => navigation.navigate('Login')}
              >
                <Text style={styles.reauthBannerBtnText}>Sign back in</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}

        {/* Hero */}
        <View style={styles.hero}>
          <View style={styles.successIconWrap}>
            <Ionicons name="checkmark-circle" size={72} color="#00FF88" />
          </View>
          <Text style={styles.heroEyebrow}>ORDER CONFIRMED</Text>
          <Text style={styles.heroTitle}>You're in. Welcome to the gear.</Text>
          <Text style={styles.heroSubtitle}>
            Your order is heading to fulfillment. You'll get an email with
            tracking once it ships. Most US orders arrive within 1–2 weeks.
          </Text>
        </View>

        {/* Order details — renders per-line breakdown for multi-item
            carts (lineItems[]), falls back to flat product/size/quantity
            rows for legacy single-item orders. */}
        <View style={styles.card}>
          <Text style={styles.cardSectionTitle}>Order Summary</Text>

          {Array.isArray(order.lineItems) && order.lineItems.length > 0 ? (
            order.lineItems.map((l, idx) => (
              <View key={idx} style={{ paddingVertical: 6 }}>
                <View style={styles.row}>
                  <Text style={styles.rowLabel} numberOfLines={2}>
                    {l.productName}{l.quantity > 1 ? `  ×${l.quantity}` : ''}
                  </Text>
                  <Text style={styles.rowValue}>
                    {formatCurrency((l.unitAmountCents || 0) * (l.quantity || 0))}
                  </Text>
                </View>
                {l.sizeLabel || l.size ? (
                  <View style={styles.row}>
                    <Text style={[styles.rowLabel, { fontSize: 12, color: '#888' }]}>
                      Size: {l.sizeLabel || l.size}
                      {l.quantity > 1 ? `  ·  ${formatCurrency(l.unitAmountCents)} each` : ''}
                    </Text>
                  </View>
                ) : null}
              </View>
            ))
          ) : (
            <>
              <View style={styles.row}>
                <Text style={styles.rowLabel}>Product</Text>
                <Text style={styles.rowValue}>{order.productName}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.rowLabel}>Size</Text>
                <Text style={styles.rowValue}>{order.size}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.rowLabel}>Quantity</Text>
                <Text style={styles.rowValue}>{order.quantity}</Text>
              </View>
            </>
          )}

          <View style={[styles.row, styles.rowTotal]}>
            <Text style={styles.rowLabelTotal}>
              Total{order.itemCount > 1 ? ` (${order.itemCount} items)` : ''}
            </Text>
            <Text style={styles.rowValueTotal}>{formatCurrency(order.totalCents)}</Text>
          </View>
        </View>

        {/* Shipping (masked) */}
        {ship && (
          <View style={styles.card}>
            <Text style={styles.cardSectionTitle}>Shipping To</Text>
            <Text style={styles.shipLine}>
              {[ship.city, ship.state, ship.zip].filter(Boolean).join(', ')}
            </Text>
            <Text style={styles.cardBodySmall}>
              Full address is on file with our fulfillment partner. If something
              looks wrong, email support@critcash.com immediately.
            </Text>
          </View>
        )}

        {/* What's next */}
        <View style={styles.card}>
          <Text style={styles.cardSectionTitle}>What happens next</Text>
          <View style={styles.timelineRow}>
            <FontAwesome5 name="check-circle" size={18} color="#00FF88" />
            <Text style={styles.timelineText}>Payment confirmed (now)</Text>
          </View>
          <View style={styles.timelineRow}>
            <FontAwesome5 name="industry" size={16} color="#FCD000" />
            <Text style={styles.timelineText}>
              Goes into production at Printful (2–5 business days)
            </Text>
          </View>
          <View style={styles.timelineRow}>
            <FontAwesome5 name="shipping-fast" size={16} color="#888" />
            <Text style={styles.timelineText}>
              Ships to your address (transit time varies by location)
            </Text>
          </View>
          <View style={styles.timelineRow}>
            <FontAwesome5 name="box" size={16} color="#888" />
            <Text style={styles.timelineText}>
              Arrives at your door (most US orders within 1–2 weeks)
            </Text>
          </View>
        </View>

        {/* Actions */}
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => navigation.navigate('Store')}
        >
          <Text style={styles.primaryButtonText}>Back to Store</Text>
        </TouchableOpacity>

        {order?.orderId && (
          <Text style={styles.orderIdLine}>
            Order ID: <Text style={{ color: '#FCD000' }}>{order.orderId}</Text>
          </Text>
        )}
      </>
    );
  };

  // ----------------------------------------------------------------------------
  // Layout
  // ----------------------------------------------------------------------------

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {loading
          ? renderLoading()
          : error
            ? renderError()
            : order?.status === 'payment_received_fulfillment_failed'
              ? renderFulfillmentFailure()
              : renderSuccess()}
        <View style={{ height: 100 }} />
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0A0A0A',
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
    paddingHorizontal: 16,
  },
  loadingText: {
    color: '#999',
    marginTop: 16,
    fontSize: 14,
  },
  errorTitle: {
    color: '#FFF',
    fontSize: 22,
    fontWeight: '700',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorBody: {
    color: '#CCC',
    fontSize: 14,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 12,
    maxWidth: 480,
  },

  // Hero
  hero: {
    alignItems: 'center',
    paddingVertical: 30,
    borderBottomWidth: 1,
    borderBottomColor: '#222',
    marginBottom: 24,
  },
  successIconWrap: {
    marginBottom: 16,
  },
  heroEyebrow: {
    color: '#00FF88',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 2,
    marginBottom: 8,
  },
  heroTitle: {
    color: '#FFF',
    fontSize: 28,
    fontWeight: '800',
    marginBottom: 8,
    textAlign: 'center',
  },
  heroSubtitle: {
    color: '#999',
    fontSize: 15,
    textAlign: 'center',
    maxWidth: 480,
    lineHeight: 22,
  },

  // Cards
  card: {
    backgroundColor: '#1A1A1A',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
  },
  cardTitle: {
    color: '#FFF',
    fontSize: 20,
    fontWeight: '700',
    marginVertical: 12,
  },
  cardSectionTitle: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  cardBody: {
    color: '#CCC',
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 10,
  },
  cardBodySmall: {
    color: '#888',
    fontSize: 12,
    lineHeight: 18,
    marginTop: 8,
  },
  linkText: {
    color: '#FCD000',
    fontWeight: '600',
  },

  // Row layout
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  rowLabel: {
    color: '#999',
    fontSize: 14,
  },
  rowValue: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
  rowTotal: {
    borderTopWidth: 1,
    borderTopColor: '#333',
    marginTop: 8,
    paddingTop: 12,
  },
  rowLabelTotal: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
  },
  rowValueTotal: {
    color: '#FCD000',
    fontSize: 18,
    fontWeight: '800',
  },

  // Shipping
  shipLine: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },

  // Timeline
  timelineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 12,
  },
  timelineText: {
    color: '#CCC',
    fontSize: 13,
    flex: 1,
  },

  // Buttons
  primaryButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 12,
  },
  primaryButtonText: {
    color: '#0A0A0A',
    fontSize: 16,
    fontWeight: '800',
  },

  orderIdLine: {
    color: '#666',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 8,
  },

  // Session-lost-after-Stripe-redirect banner (iOS Safari ITP edge case).
  // Gold border on dark for visibility; sign-in CTA inline so the user
  // can recover without leaving the screen.
  reauthBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#2a2200',
    borderColor: '#FCD00080',
    borderWidth: 1,
    borderRadius: 10,
    padding: 14,
    marginBottom: 18,
  },
  reauthBannerText: {
    color: '#FCD000',
    fontSize: 13,
    lineHeight: 19,
  },
  reauthBannerBtn: {
    alignSelf: 'flex-start',
    backgroundColor: '#FCD000',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    marginTop: 10,
  },
  reauthBannerBtnText: {
    color: '#0A0A0A',
    fontWeight: '800',
    fontSize: 13,
  },
});
